/**
 * CrossMatterChangeDetailPage - Full entity-level change detail for a single matter
 *
 * Shows all pending changes grouped by entity type (providers, treatment requests,
 * coverages, tasks, task updates, staff notes) with old/current diffs.
 */

import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetCrossMatterChangeDetailQuery } from '@/services/api/crossMatterChangesService';

import { CoverageChangeSection } from './components/CoverageChangeSection';
import { DocumentChangeSection } from './components/DocumentChangeSection';
import { DocumentComparisonPanel } from './components/DocumentComparisonPanel';
import { ProviderChangeSection } from './components/ProviderChangeSection';
import { StaffNoteChangeSection } from './components/StaffNoteChangeSection';
import { TaskChangeSection } from './components/TaskChangeSection';
import { TreatmentRequestChangeSection } from './components/TreatmentRequestChangeSection';

export const CrossMatterChangeDetailPage = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useGetCrossMatterChangeDetailQuery(
    { matterId: matterId! },
    { skip: !matterId },
  );

  const providerIds = useMemo(
    () => new Set((data?.providers ?? []).map((p) => p.entity_id)),
    [data?.providers],
  );

  const orphanedTreatmentRequests = useMemo(
    () =>
      (data?.treatment_requests ?? []).filter((tr) => {
        const providerId = (tr.current ?? tr.old)?.provider_id;
        return !providerId || !providerIds.has(providerId);
      }),
    [data?.treatment_requests, providerIds],
  );

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500">Failed to load matter change details.</p>
        <button
          type="button"
          onClick={() => navigate('/cross-matter-changes')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Back to list
        </button>
      </div>
    );
  }

  const totalEntities =
    data.providers.length +
    data.treatment_requests.length +
    data.coverages.length +
    data.tasks.length +
    data.task_updates.length +
    data.staff_notes.length +
    data.documents.length;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-7xl">
        {/* Back nav */}
        <button
          type="button"
          onClick={() => navigate('/cross-matter-changes')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Cross-Matter Changes
        </button>

        <div className="flex flex-col gap-6 xl:flex-row">
          {/* Left: entity sections */}
          <div className="min-w-0 flex-1 space-y-6">
            {/* Header */}
            <div className="rounded-lg border border-gray-200 bg-white px-6 py-5 shadow-sm">
              <h1 className="text-lg font-semibold text-gray-900">
                Matter Change Detail
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {totalEntities}{' '}
                {totalEntities === 1 ? 'entity' : 'entities'} with pending
                changes
              </p>
            </div>

            {/* Entity sections */}
            <div className="space-y-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              {totalEntities === 0 && (
                <p className="py-8 text-center text-sm text-gray-500">
                  No pending entity changes for this matter.
                </p>
              )}

              <ProviderChangeSection
                providers={data.providers}
                treatmentRequests={data.treatment_requests}
              />
              {orphanedTreatmentRequests.length > 0 && (
                <TreatmentRequestChangeSection
                  treatmentRequests={orphanedTreatmentRequests}
                />
              )}
              <CoverageChangeSection coverages={data.coverages} />
              <TaskChangeSection
                tasks={data.tasks}
                taskUpdates={data.task_updates}
              />
              <StaffNoteChangeSection staffNotes={data.staff_notes} />
              <DocumentChangeSection documents={data.documents} />
            </div>
          </div>

          {/* Right: document comparison panel (sticky) */}
          <div className="shrink-0 xl:w-96">
            <div className="sticky top-8">
              <DocumentComparisonPanel
                matterId={matterId!}
                documents={data.documents}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
